import { useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { CONFIG } from '../../config';
import { hrefFor } from '../../routers';
import { PaymentSource } from '../../constants';
import { NULL_USER } from '../../lib/user';
import { reportError } from '../../lib/reportError';
import {
  createBrainTreeClient,
  createPaypalCheckoutInstance,
  waitForPayPalCheckoutScripts
} from '../../lib/braintree';
import {
  loadPayPalScript,
  loadBraintreeScript,
  loadBraintreePayPalCheckoutScript
} from '../../lib/loadScript';
import { useCountry } from '../../components/CountrySelector';
import { useErrorModal } from '../useErrorModal';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../useCart';
import { useUser } from '../useUser';
import { CreatePayPalPaymentMethodMutationShippingAddress } from './createPayPalPaymentMethodMutation.rest';
import { useCreatePayPalPaymentMethodMutation } from './useCreatePayPalPaymentMethodMutation';

type PayPalResponse = {
  type: 'PayPalAccount';
  nonce: string;
  details: {
    countryCode: string;
    email: string;
    firstName: string;
    lastName: string;
    payerID: string;
    phone: string;
    shippingAddress: CreatePayPalPaymentMethodMutationShippingAddress;
  };
};

type PayPalButtonArgs = {
  paypalClientToken: string;
  elementSelector: string;
  height?: number | string;
  onSubmit: (response: PayPalResponse) => void;
};

const renderPaypalButton = async ({
  paypalClientToken,
  elementSelector,
  height,
  onSubmit
}: PayPalButtonArgs) => {
  try {
    const client = await createBrainTreeClient(paypalClientToken);
    const paypalCheckoutInstance = await createPaypalCheckoutInstance(client);

    window.paypal.Button.render(
      {
        env: CONFIG.ENVIRONMENT === 'prod' ? 'production' : 'sandbox',
        commit: false,
        style: {
          shape: 'rect',
          color: 'white',
          size: 'responsive',
          height,
          width: 'auto',
          label: 'paypal',
          tagline: false
        },
        payment: () =>
          paypalCheckoutInstance.createPayment({
            flow: 'vault',
            enableShippingAddress: true,
            shippingAddressEditable: true
          }),
        onAuthorize: (data: unknown) =>
          paypalCheckoutInstance.tokenizePayment(
            data,
            (error: Error | undefined, payload: PayPalResponse) => {
              if (error) {
                reportError(new Error(`Error processing PayPal payment method: ${error}`));
                return;
              }
              onSubmit(payload);
            }
          )
      },
      elementSelector
    );
  } catch (error) {
    reportError(error);
  }
};

type UsePayPalButtonArgs = {
  elementSelector: string;
  height?: number | string;
};

export const usePayPalButton = ({ elementSelector, height }: UsePayPalButtonArgs) => {
  const location = useLocation();
  const history = useHistory();
  const { showErrorModal } = useErrorModal();
  const {
    country: { currencyCode }
  } = useCountry();
  const { setUserData } = useUser();
  const {
    cart: {
      attributes: { paypalClientToken, paymentSource }
    }
  } = useCart();
  const [
    createPaymentMethod,
    { loading: creatingPaymentMethod }
  ] = useCreatePayPalPaymentMethodMutation();
  const [updateCart, { loading: updatingCart }] = useUpdateCartMutation();

  const handleSubmit = useCallback(
    async (data: PayPalResponse) => {
      try {
        const result = await createPaymentMethod({
          variables: {
            input: {
              data: {
                nonce: data.nonce,
                email: data.details.email,
                first_name: data.details.firstName,
                last_name: data.details.lastName,
                payerID: data.details.payerID,
                shipping_address: data.details.shippingAddress,
                currency: currencyCode
              }
            }
          }
        });

        if (!result.data?.createPayPalPaymentMethod.success) {
          throw new Error('Failed to create the PayPal payment method.');
        }

        if (result.data.createPayPalPaymentMethod.guestAccessToken) {
          setUserData({
            ...NULL_USER,
            accessToken: result.data.createPayPalPaymentMethod.guestAccessToken,
            remember: true
          });
        }

        if (paymentSource !== PaymentSource.PayPal) {
          await updateCart(CartUpdateAction.PaymentSource, {
            [CartUpdatePropertyName.PaymentSource]: PaymentSource.PayPal
          });
        }

        if (location.pathname !== hrefFor.CheckoutPage()) {
          history.push(hrefFor.CheckoutPage());
        }
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    },
    [
      createPaymentMethod,
      showErrorModal,
      currencyCode,
      paymentSource,
      updateCart,
      history,
      location.pathname,
      setUserData
    ]
  );

  useEffect(() => {
    loadPayPalScript();
    loadBraintreeScript();
    loadBraintreePayPalCheckoutScript();

    const renderButton = async () => {
      if (!paypalClientToken) return;
      await waitForPayPalCheckoutScripts();
      renderPaypalButton({
        paypalClientToken,
        elementSelector,
        height,
        onSubmit: handleSubmit
      });
    };

    renderButton();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading: creatingPaymentMethod || updatingCart };
};
